import { useTranslation } from 'next-i18next';
import { useMemo, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useDebounceCallback } from 'usehooks-ts';

import { logWarning } from '@dx-ui/framework-logger';
import type { AddressOptionsAddressType } from '@dx-ui/gql-types';
import { FormInput, FormSelect } from '@dx-ui/osc-form';
import { generateError } from '@dx-ui/utilities-generate-error';

import { useCountries_AddressOptionsQuery } from './gql/queries';
import { AutoComplete, getAutoCompleteValue } from './helpers/autocomplete';
import { useHelpers } from './helpers/useHelpers';
import { SkeletonFields } from './helpers/utility-components';

export type AddressFieldsOptions = {
  addressType?: AddressOptionsAddressType;
  fieldNamePrefix?: string;
  tryToDisplayCityAndState?: boolean;
};

export type AddressFieldsProps = {
  code: string;
  language?: string;
  addressFieldsOptions?: AddressFieldsOptions;
};

const defaultOptions = {
  addressType: 'nonBusiness' as AddressOptionsAddressType,
  fieldNamePrefix: 'address',
  tryToDisplayCityAndState: false,
};

export function AddressFields({
  code,
  language = 'en',
  addressFieldsOptions = defaultOptions,
}: AddressFieldsProps) {
  const mergedAddressFieldsOptions = useMemo(
    () => ({ ...defaultOptions, ...addressFieldsOptions }),
    [addressFieldsOptions]
  );
  const { t } = useTranslation('osc-address-fields');
  const { setValue, watch, register } = useFormContext() || {};
  const { getFieldKey, ...form } = useHelpers(mergedAddressFieldsOptions);
  const state = watch(getFieldKey('state'));
  const city = watch(getFieldKey('city'));
  const fetchGeocodeQuery = form.useFetchGeocodeQuery(language);
  const [isGeolocationDataLoading, setGeolocationDataLoading] = useState(false);
  const [hasGeocodeDataLookupFailed, setGeocodeDataLookupFailed] = useState(false);

  const {
    data: addressFieldData,
    isLoading: isAddressOptionsLoading,
    error: addressOptionsQueryError,
  } = useCountries_AddressOptionsQuery(
    {
      code,
      language,
      type: mergedAddressFieldsOptions.addressType,
    },
    { enabled: form.isValidCountryCode(code) }
  );

  const fields = addressFieldData?.countries?.[0]?.addressOptions ?? [];
  const isPostalCodeLookupEnabled =
    addressFieldData?.countries?.[0]?.enrollmentConfig?.isPostalCodeLookupEnabled;
  const cityErrorLabel =
    fields.find((field) => field.name_noTx === 'city')?.label.toLowerCase() ||
    t('city.fallbackLabel');
  const postalCodeErrorLabel =
    fields.find((field) => field.name_noTx === 'postalCode')?.label.toLowerCase() ||
    t('postalCode.fallbackPostalCodeLabel');
  const stateErrorLabel =
    fields.find((field) => field.name_noTx === 'state')?.label.toLowerCase() ||
    t('state.fallbackLabel');

  const geocodeErrorLabels = {
    cityErrorLabel,
    postalCodeErrorLabel: code === 'US' ? t('postalCode.zipCode') : postalCodeErrorLabel,
    stateErrorLabel,
  };

  const states =
    addressFieldData?.countries?.[0]?.addressOptions?.find(
      (field) => field.name_noTx.toLowerCase() === 'state'
    )?.options ?? [];

  const shouldHideCityAndStateFields =
    isPostalCodeLookupEnabled &&
    !hasGeocodeDataLookupFailed &&
    !mergedAddressFieldsOptions.tryToDisplayCityAndState;

  const debouncePostalCode = useDebounceCallback(
    async (regexValidation: string, postalCodeValue: string) => {
      if (form.isValidPostalCode(regexValidation, postalCodeValue)) {
        setGeolocationDataLoading(true);
        await fetchGeocodeQuery({ postalCode: postalCodeValue })
          .then((result) => {
            const address = result?.geocode?.match?.address;
            const { city: geocodeCity, state: geocodeState } = address || {};
            if (!geocodeCity || !geocodeState) {
              setGeocodeDataLookupFailed(true);
              form.setCityStateOnGeocodeFailure(geocodeErrorLabels);
            } else {
              form.clearAddressFieldErrors(['postalCode', 'city', 'state']);
              setValue(getFieldKey('city'), geocodeCity);
              setValue(getFieldKey('state'), geocodeState);
              setGeocodeDataLookupFailed(false);
            }
          })
          .catch((geocodeError) => {
            form.handleGeocodeError(geocodeError, geocodeErrorLabels);
            setGeocodeDataLookupFailed(true);
          })
          .finally(() => {
            setGeolocationDataLoading(false);
          });
      }
    },
    400
  );

  if (addressOptionsQueryError) {
    logWarning(
      'OSC_ADDRESS_FIELDS',
      generateError(addressOptionsQueryError),
      `Error on ${useCountries_AddressOptionsQuery.name} at libs/osc/address-fields`
    );
  }

  if (!fields || !form.isValidCountryCode(code)) {
    return null;
  }

  if (isAddressOptionsLoading) {
    const numberOfFields =
      addressFieldsOptions?.addressType === 'nonBusinessShortened'
        ? 1
        : shouldHideCityAndStateFields
          ? 3
          : 5;
    return <SkeletonFields numberOfFields={numberOfFields} />;
  }

  return (
    <>
      {fields.map(
        ({ name_noTx: name, label, required, maxLength, type_noTx: type, regexValidation }) => {
          const isPostalCodeLookupField =
            name === 'postalCode' && isPostalCodeLookupEnabled && regexValidation;

          if (shouldHideCityAndStateFields && (name === 'state' || name === 'city')) {
            return <input type="hidden" key={getFieldKey(name)} {...register(getFieldKey(name))} />;
          }

          if (type === 'dropdown') {
            return (
              <FormSelect
                label={label}
                labelClassName="w-full"
                name={getFieldKey(name)}
                key={getFieldKey(name)}
                autoComplete={AutoComplete.STATE}
                optional={!required}
                registerOptions={{
                  required: required && t('state.required', { stateLabel: stateErrorLabel }),
                }}
              >
                <option disabled hidden value="">
                  {t('pleaseSelect')}
                </option>
                {states.map((state) => (
                  <option value={state.value ?? ''} key={state.value}>
                    {state.label}
                  </option>
                ))}
              </FormSelect>
            );
          }

          return (
            <FormInput
              autoComplete={getAutoCompleteValue(name)}
              label={label}
              labelClassName="w-full flex-grow"
              name={getFieldKey(name)}
              optional={!required}
              key={getFieldKey(name)}
              loading={isGeolocationDataLoading && name === 'postalCode'}
              registerOptions={{
                required:
                  required &&
                  !isPostalCodeLookupField &&
                  t(`${name}.required`, {
                    context: code === 'JP' ? 'JP' : null,
                    ...geocodeErrorLabels,
                  }),
                maxLength: {
                  value: maxLength as number,
                  message: t('maxLengthExceeded', { maxLength: maxLength as number }),
                },
                validate: (value: string) => {
                  if (isPostalCodeLookupField) {
                    if (!value) {
                      return t('postalCode.required', {
                        ...geocodeErrorLabels,
                      });
                    }
                    if (!form.isValidPostalCode(regexValidation, value)) {
                      return t('postalCode.error', {
                        ...geocodeErrorLabels,
                      });
                    }
                    if (mergedAddressFieldsOptions?.addressType === 'nonBusinessShortened') {
                      if (!state) {
                        return t('postalCode.error_lookup_shortenEnrollment', {
                          ...geocodeErrorLabels,
                        });
                      }
                    } else if (!city || !state) {
                      return t('postalCode.error_lookup', { ...geocodeErrorLabels });
                    }
                    return true;
                  }
                },
                onChange: (event) => {
                  if (isPostalCodeLookupField) {
                    const postalCodeValue = event.target.value.trim();
                    void debouncePostalCode(regexValidation, postalCodeValue);
                  }
                },
              }}
            />
          );
        }
      )}
    </>
  );
}
